/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { Box, Grid, IconButton, List, ListItem, ListItemText, TextField } from "@mui/material";

// Images
import logoSlack from "assets/images/small-logos/logo-slack.svg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";
import logoAtlassian from "assets/images/small-logos/logo-atlassian.svg";
import logoAsana from "assets/images/small-logos/logo-asana.svg";

// Material Dashboard 2 PRO React components
import { useMaterialUIController } from "context";
import ApiCalls from "services/flick_erp_api_call";
import { Assignment, Close, Delete } from "@mui/icons-material";
import { authFetch, base_url } from "services";
import { OrganizationDocuments } from "services/apis";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import { useTheme } from "@mui/material/styles";

function Documents(data, Employee) {
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
  const theme = useTheme();
  const palette = theme.palette;
	const fileInputRef = useRef(null);

	const [slack2FA, setSlack2FA] = useState(true);
	const [spotify2FA, setSpotify2FA] = useState(true);
	const [atlassian2FA, setAtlassian2FA] = useState(true);
	const [asana2FA, setAsana2FA] = useState(false);
	const [contentID, setContentID] = useState(null);
	const [resume, setResume] = useState([]);
	const [cvError, setCVError] = useState('');
	const [documentName, setDocumentName] = useState("");
	const [employee, setEmployee] = useState(data?.data);

	const handleSetSlack2FA = () => setSlack2FA(!slack2FA);
	const handleSetSpotify2FA = () => setSpotify2FA(!spotify2FA);
	const handleSetAtlassian2FA = () => setAtlassian2FA(!atlassian2FA);
	const handleSetAsana2FA = () => setAsana2FA(!asana2FA);
	const [fileValidationMessage, setFileValidationMessage] = useState("");

  const ui = {
    card: darkMode ? palette.background.card : colors.white.main,
    soft: darkMode ? palette.background.muted : rgba(colors.background.elevated, 0.56),
    border: darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.1),
    title: darkMode ? palette.text.focus : colors.dark.main,
    body: darkMode ? palette.text.main : colors.text.main,
    shadow: darkMode ? "0 18px 40px rgba(2, 6, 23, 0.28)" : `0 18px 40px ${rgba(colors.dark.main, 0.06)}`,
  };

	useEffect(() => {
		getContentTypeList();
	}, []);


	const getEmployee = async () => {
		var res = await ApiCalls.getEmployee(data?.data?.id);

		if (res.success && res.code == 200) {
			setEmployee(res.data);
		} else {
		}

	}


	const handleUpload = async () => {
		if (!resume) {
			setFileValidationMessage("Please select a file.");
			return;
		}


		const objectId = data?.data?.id;
		const document_Name = documentName;

		const formData = new FormData();
		formData.append("content_type", contentID);
		formData.append("object_id", objectId);
		formData.append("name", document_Name);
		// formData.append("file", resume); 
		resume.forEach((file) => {
			formData.append("file", file);
		});
		try {
			const response = await ApiCalls.postDocument(formData);
			if (response.success && response.code === 201) {
				setFileValidationMessage("");
				// setSelectedFile(null);
				setResume([]);
				getEmployee()
				setDocumentName("");
				if (fileInputRef.current) {
					fileInputRef.current.value = "";
				}
				alert("Document uploaded successfully!");
			} else {
				setFileValidationMessage("Upload failed. Please try again.");
			}
		} catch (error) {
			setFileValidationMessage("An error occurred during upload.");
			console.error(error);
		}
	};

	const handleFileSelect = (e) => {
		const files = Array.from(e.target.files);
		const allowedExtensions = ["pdf", "docx"];
		const invalidFiles = files?.filter((file) => {
			const extension = file.name.split(".").pop().toLowerCase();
			return !allowedExtensions.includes(extension);
		});
		if (invalidFiles?.length > 0) {
			setCVError("Only PDF or DOCX files are allowed.");
			return;
		}
		setResume((prev) => [...prev, ...files]); // Add to existing files
		setCVError("");
	};
	const removeFile = (index) => {
		const updated = resume.filter((_, i) => i !== index);
		setResume(updated);
	};

	const getContentTypeList = async () => {
		const response = await ApiCalls.getContentList();
		if (response.success && response.code === 200) {
			const value = response.data.content_types.find((res) => res.name === 'employee')
			setContentID(value?.id);
		}
	}

	const handleDelete = async (docId) => {
		try {
			const response = await authFetch(`${base_url}${OrganizationDocuments}${docId}/`, {
				method: "Delete",
				headers: {
					"Content-Type": "application/json",
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to delete Document: ${response.statusText}`);
			}
			getEmployee();

		} catch (error) {
			console.error("Error updating Employee Document:", error);
		}
	}


	return (
		<Card
			id="documents"
			sx={{
				borderRadius: "28px",
				border: `1px solid ${ui.border}`,
        backgroundColor: ui.card,
				boxShadow: ui.shadow,
			}}
		>
			<MDBox
				p={3}
				lineHeight={1}
				sx={{ borderBottom: `1px solid ${ui.border}` }}
			>
				<MDBox display="flex" gap={3}>
				<MDBox
					sx={{
						width: 48,
						height: 48,
						borderRadius: "16px",
						display: "grid",
						placeItems: "center",
						background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
						color: colors.white.main,
					}}
				>
					<Assignment />
				</MDBox>
				<MDBox mb={1}>
					<MDTypography variant="h5" sx={{ color: ui.title, marginBottom:"2px" }}>
						Documents
					</MDTypography>
					<MDTypography variant="button" sx={{ color: ui.body }} >
						Upload the required document here.
					</MDTypography>
				</MDBox>
				</MDBox>
				<br />

				<MDBox>
					<TextField
						label="Document Name"
						variant="standard"
						fullWidth
						value={documentName}
						onChange={(e) => setDocumentName(e.target.value)}
						sx={{ mb: 2 }}
					/>

				</MDBox>

				<Grid container spacing={3}>
					<Grid item xs={6}>
						<TextField
							inputRef={fileInputRef}
							InputLabelProps={{ shrink: true }}
							error={!!cvError}
							helperText={cvError}
							label="Resume"
							variant="standard"
							fullWidth
							type="file"
							inputProps={{ multiple: true }}
							onChange={handleFileSelect}
						/>
						{resume.length > 0 && (
							<Box
								sx={{
									maxHeight: 150,
									overflowY: 'auto',
									border: `1px solid ${ui.border}`,
									borderRadius: 2,
									p: 1,
									backgroundColor: ui.soft,
								}}
							>
								<List dense>
									{resume.map((file, index) => (
										<ListItem
											key={index}
											secondaryAction={
												<IconButton edge="end" onClick={() => removeFile(index)}>
													<Close />
												</IconButton>
											}
										>
											<ListItemText primary={file.name} />
										</ListItem>
									))}
								</List>
							</Box>
						)}
					</Grid>
					<Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
						<br />
						<MDButton color='info' size='small' sx={{ fontSize: '12px', minWidth: '120px', height: '40px', borderRadius: "14px" }} onClick={handleUpload}>Upload</MDButton>
					</Grid>
				</Grid>

			</MDBox>

			{employee?.documents?.length > 0 && (
				<>
					<Divider sx={{ my: 3 }} />
					<MDTypography variant="h5" gutterBottom paddingLeft={3} sx={{ color: ui.title }}>
						Uploaded Documents
					</MDTypography>
					<Grid container spacing={2} sx={{ mt: 1, px: 2, pb: 3 }}>
						{employee?.documents?.map((doc, index) => (
							<Grid item xs={12} sm={6} md={6} key={index}>
								<Card
									variant="outlined"
									sx={{
										p: 2,
										height: "100%",
										display: "flex",
										justifyContent: "space-between",
										borderRadius: "22px",
                    backgroundColor: ui.card,
										borderColor: ui.border,
										boxShadow: darkMode ? "0 10px 24px rgba(2, 6, 23, 0.22)" : `0 10px 24px ${rgba(colors.dark.main, 0.04)}`,
									}}
								>
									<MDBox display="flex" justifyContent='space-between' marginBottom={1}>
										<MDTypography variant="subtitle1" fontWeight="bold" gutterBottom>
											{doc.name || `Document ${index + 1}`}
										</MDTypography>
										<IconButton
											variant="outlined"
											color="error"
											size="small"
											fullWidth
											onClick={() => handleDelete(doc.id)}
											startIcon={<Icon>delete</Icon>}
										>
											<Delete />
										</IconButton></MDBox>


									<Box display="flex" justifyContent="space-between" gap={1} mt="auto">
										{/* <MDButton
                                            component="a"
                                            href={doc.file}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="outlined"
                                            color="info"
                                            size="small"
                                            fullWidth
                                            startIcon={<Icon>visibility</Icon>}
                                        > */}
										<MDButton
											onClick={() => window.open(doc.file, '_blank')}
											variant="outlined"
											color="info"
											size="small"
											fullWidth
											startIcon={<Icon>visibility</Icon>}
										>
											View
										</MDButton>
										<MDButton
											component="a"
											href={doc.file}
											download
											variant="gradient"
											color="info"
											size="small"
											fullWidth
											startIcon={<Icon>download</Icon>}
										>
											Download
										</MDButton>
									</Box>
								</Card>

							</Grid>
						))}
					</Grid>
				</>
			)}

		</Card>
	);
}

export default Documents;
